import { useEffect, useState } from 'react';
import type { ApiClient } from '../../api/client';
import type { IntegrationCall, IntegrationState } from './context';

type Preview = { id: string; tool: string; arguments_json: string; arguments_hash: string; expires_at: string; preview_only: true };

/** The browser approves a frozen digest. It never dispatches or retries a write. */
export default function IntegrationCallReview({ call, state, apiClient, refresh }: {
  call: IntegrationCall; state: IntegrationState; apiClient: ApiClient; refresh: () => Promise<void>;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!['proposed', 'ready'].includes(call.status) || !call.expires_at) return;
    const deadline = Date.parse(call.expires_at);
    const timer = setInterval(() => { const current = Date.now(); setNow(current); if (current >= deadline) clearInterval(timer); }, 1000);
    return () => clearInterval(timer);
  }, [call.status, call.expires_at]);
  const name = state.available.find(item => item.id === call.integration_id)?.name ?? call.integration_id;
  const pending = call.status === 'proposed';
  const expired = call.expires_at !== null && Date.parse(call.expires_at) <= now;
  const load = async () => {
    setBusy(true); setError(null);
    try {
      const value = await apiClient.get<Preview>(`/api/assistant/gateway/integrations/calls/${call.id}`);
      if (value.id !== call.id || value.tool !== call.tool || value.arguments_hash !== call.arguments_hash || typeof value.arguments_json !== 'string' || !value.preview_only) throw new Error('Write preview changed');
      setPreview(value);
    } catch { setError('The exact change could not be loaded. Refresh the request before approving.'); }
    finally { setBusy(false); }
  };
  const answer = async (decision: 'approve' | 'deny') => {
    setBusy(true); setError(null);
    try {
      await apiClient.post('/api/assistant/gateway/integrations/calls/decide', {
        id: call.id, decision, ...(decision === 'approve' ? { arguments_hash: preview?.arguments_hash } : {}),
      });
      await refresh();
    } catch { setError('The saved decision is not confirmed. Refresh its status; this review will not repeat the app call.'); }
    finally { setBusy(false); }
  };
  const title = ({ proposed: 'Review an app change', ready: 'Exact change approved', denied: 'Change declined', expired: 'Change expired', completed: 'App response received', outcome_unknown: 'Waiting for a confirmed app result' })[call.status];
  return <article className="assistant-review-card assistant-integration-call">
    <div className="assistant-review-kicker">{title}</div>
    <h3>{name} · {call.tool.replaceAll('_', ' ')}</h3>
    <p>{call.purpose}</p>
    {pending ? <>
      <p>This writes to your app account. Review the exact contents before allowing this change.</p>
      {preview ? <pre className="assistant-integration-arguments" aria-label="Exact app change">{preview.arguments_json.replace(/[\u202a-\u202e\u2066-\u2069]/g, character => `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`)}</pre>
        : <button className="ghost" disabled={busy || expired} onClick={() => { void load(); }}>Review exact change</button>}
      {call.expires_at && <p className="assistant-review-scope">Request expires {new Date(call.expires_at).toLocaleString()}. Approval lasts up to five minutes within your tool access.</p>}
      {expired && <p role="status">This proposal expired. It cannot be approved.</p>}
      {call.access_active === false && <p role="status">Current tool access is required before this change can be approved. You can still decline it.</p>}
      <div className="assistant-review-buttons"><button disabled={busy || expired || call.access_active === false || !preview || preview.arguments_hash !== call.arguments_hash} onClick={() => { void answer('approve'); }}>Approve this exact change</button>
        <button className="ghost" disabled={busy} onClick={() => { void answer('deny'); }}>Not now</button></div>
    </> : call.status === 'ready' ? <>
      <p>The assistant can submit only the reviewed contents while this approval and its tool access remain valid.</p>
      <p>If its turn already ended, ask the assistant to continue before this approval expires.</p>
      {call.expires_at && <p>Approval expires {new Date(call.expires_at).toLocaleString()}.</p>}
      <button className="ghost" disabled={busy} onClick={() => { void answer('deny'); }}>Withdraw before sending</button>
    </> : call.status === 'outcome_unknown' ? <p>The app may have received this call. Check its state before requesting another change. This call will not be replayed.</p>
      : call.status === 'completed' ? <p>Check the assistant’s reply for the app’s result. A response alone does not prove the requested change succeeded.</p> : null}
    {error && <p role="alert">{error}</p>}
  </article>;
}
